import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const accountRef = useRef(null);
  accountRef.current = user?.id || null;
  const [settings, setSettings] = useState(null);
  const [business, setBusiness] = useState(null);

  const refresh = useCallback(async () => {
    const accountId = accountRef.current;
    if (!accountId || !localStorage.getItem("token")) return;
    const [s, b] = await Promise.all([api.get("/settings"), api.get("/business-profile")]);
    if (accountRef.current !== accountId) return;
    setSettings(s.data);
    setBusiness(b.data);
  }, []);

  useEffect(() => {
    setSettings(null);
    setBusiness(null);
    document.documentElement.style.removeProperty("--finlend-font");
    if (user && !user.must_change_password) refresh().catch(() => {});
  }, [user, refresh]);

  const currency = settings?.currency || "₹";
  const dateFormat = settings?.date_format || "DD/MM/YYYY";

  // ==========================================================
  // Global FinLend font + typing preferences
  // ==========================================================
  useEffect(() => {
    if (!settings) return;

    const fonts = {
      inter: "'Inter', sans-serif",
      outfit: "'Outfit', sans-serif",
      arial: "Arial, sans-serif",
      georgia: "Georgia, serif",
      times: "'Times New Roman', Times, serif",
    };

    const selectedFont =
      fonts[settings.font_style || "inter"] || fonts.inter;

    document.documentElement.style.setProperty(
      "--finlend-font",
      selectedFont
    );

    const spellCheck = settings.spell_check !== false;
    const autoCorrect = settings.auto_correct !== false;
    const autoCapitalize = settings.auto_capitalize !== false;

    const protectedWords = [
      "phone",
      "mobile",
      "contact",
      "aadhaar",
      "aadhar",
      "pan",
      "email",
      "password",
      "amount",
      "principal",
      "interest",
      "emi",
      "currency",
      "loan_id",
      "loan-id",
      "loan number",
      "loan-number",
      "receipt",
      "prefix",
      "upi",
      "account",
      "ifsc",
      "gst",
      "license",
      "licence",
      "number",
      "date",
    ];

    const shouldProtect = (el) => {
      if (!el) return true;

      const type = (el.getAttribute("type") || "text").toLowerCase();

      if (
        [
          "number",
          "tel",
          "date",
          "datetime-local",
          "time",
          "month",
          "week",
          "password",
          "email",
        ].includes(type)
      ) {
        return true;
      }

      const descriptor = [
        el.name,
        el.id,
        el.placeholder,
        el.getAttribute("data-testid"),
        el.getAttribute("aria-label"),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return protectedWords.some((word) =>
        descriptor.includes(word)
      );
    };

    const applyTypingSettings = (root = document) => {
      const elements = [];

      if (
        root instanceof HTMLElement &&
        (root.matches("input") ||
          root.matches("textarea") ||
          root.isContentEditable)
      ) {
        elements.push(root);
      }

      if (root.querySelectorAll) {
        elements.push(
          ...root.querySelectorAll(
            'input, textarea, [contenteditable="true"]'
          )
        );
      }

      elements.forEach((el) => {
        if (shouldProtect(el)) {
          el.setAttribute("spellcheck", "false");
          el.setAttribute("autocorrect", "off");
          el.setAttribute("autocapitalize", "off");
          return;
        }

        el.setAttribute(
          "spellcheck",
          spellCheck ? "true" : "false"
        );

        el.setAttribute(
          "autocorrect",
          autoCorrect ? "on" : "off"
        );

        el.setAttribute(
          "autocapitalize",
          autoCapitalize ? "sentences" : "off"
        );
      });
    };

    applyTypingSettings();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            applyTypingSettings(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [
    settings,
    settings?.font_style,
    settings?.spell_check,
    settings?.auto_correct,
    settings?.auto_capitalize,
  ]);

  return (
    <SettingsContext.Provider value={{ settings, business, refresh, setSettings, setBusiness, currency, dateFormat }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
